import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, TextField, 
  FormControl, InputLabel, Select, MenuItem, Chip, Dialog, 
  DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel,
  List, ListItem, ListItemText, ListItemIcon, InputAdornment,
  Tabs, Tab, Alert, Snackbar, useTheme, useMediaQuery
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  AddOutlined as AddIcon,
  AutoAwesomeOutlined as AIIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  HomeOutlined as HomeIcon,
  ApartmentOutlined as ApartmentIcon,
  LocationOnOutlined as LocationIcon,
  CheckCircleOutline as CheckIcon
} from '@mui/icons-material';
import PhotoUpload from './PhotoUpload';
import api from '../api';

const PropertyAdvertising = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [activeTab, setActiveTab] = useState(0);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [listingDialog, setListingDialog] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [creatingListing, setCreatingListing] = useState(false);
  
  // Listing form state
  const [listingData, setListingData] = useState({
    title: '',
    description: '',
    price: '',
    bedrooms: '',
    bathrooms: '',
    squareFeet: '',
    propertyType: 'apartment',
    features: [],
    photos: [],
    platforms: {
      craigslist: true,
      facebook: true,
      zillow: false,
      apartments: false,
      realtor: false
    }
  });

  // AI-generated content
  const [aiContent, setAiContent] = useState({
    title: '',
    description: '',
    highlights: []
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await api.get('/api/properties');
      const propertiesWithUnits = await Promise.all(
        response.data.map(async (property) => {
          try {
            const unitsResponse = await api.get(`/api/properties/${property.id}/units`);
            return {
              ...property,
              units: unitsResponse.data || []
            };
          } catch (error) {
            console.error(`Failed to fetch units for property ${property.id}:`, error);
            return {
              ...property,
              units: []
            };
          }
        })
      );
      setProperties(propertiesWithUnits);
    } catch (error) {
      console.error('Failed to fetch properties:', error);
      setSnackbar({ open: true, message: 'Failed to fetch properties', severity: 'error' });
    }
  };

  const generateAIContent = async () => {
    try {
      // Mock AI content generation
      const mockContent = {
        title: `Beautiful ${listingData.bedrooms}BR ${listingData.propertyType} in Prime Location`,
        description: `Discover this stunning ${listingData.bedrooms}-bedroom, ${listingData.bathrooms}-bathroom ${listingData.propertyType} located in a highly desirable neighborhood. This well-maintained unit features modern amenities, plenty of natural light, and convenient access to shopping, dining, and transportation. Perfect for professionals, students, or small families seeking comfort and convenience.`,
        highlights: [
          `${listingData.bedrooms} spacious bedrooms`,
          `${listingData.bathrooms} modern bathrooms`,
          `${listingData.squareFeet} sq ft of living space`,
          'Updated kitchen with modern appliances',
          'In-unit laundry facilities',
          'Pet-friendly community',
          'On-site parking available',
          'Close to public transportation'
        ]
      };
      
      setAiContent(mockContent);
      setSnackbar({ open: true, message: 'AI content generated successfully!', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to generate AI content', severity: 'error' });
    }
  };

  const createListing = async () => {
    try {
      setCreatingListing(true);
      
      // Validation
      if (!selectedProperty) {
        setSnackbar({ open: true, message: 'Please select a property', severity: 'error' });
        return;
      }

      if (!listingData.price) {
        setSnackbar({ open: true, message: 'Please fill in the price', severity: 'error' });
        return;
      }

      const newListing = {
        propertyId: selectedProperty.id,
        title: listingData.title || aiContent.title,
        description: listingData.description || aiContent.description,
        price: parseFloat(listingData.price),
        bedrooms: parseInt(listingData.bedrooms),
        bathrooms: parseInt(listingData.bathrooms),
        squareFeet: parseInt(listingData.squareFeet),
        propertyType: listingData.propertyType,
        features: aiContent.highlights || [],
        photos: listingData.photos,
        platforms: listingData.platforms,
        aiContent,
        status: 'draft',
        createdAt: new Date()
      };
      
      // Mock API call - replace with actual API call
      console.log('Creating listing:', newListing);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setListingDialog(false);
      setSnackbar({ open: true, message: 'Listing created successfully!', severity: 'success' });
      
      // Reset form
      setListingData({
        title: '',
        description: '',
        price: '',
        bedrooms: '',
        bathrooms: '',
        squareFeet: '',
        propertyType: 'apartment',
        features: [],
        photos: [],
        platforms: {
          craigslist: true,
          facebook: true,
          zillow: false,
          apartments: false,
          realtor: false
        }
      });
      setAiContent({ title: '', description: '', highlights: [] });
      setSelectedProperty(null);
    } catch (error) {
      console.error('Error creating listing:', error);
      setSnackbar({ open: true, message: 'Failed to create listing', severity: 'error' });
    } finally {
      setCreatingListing(false);
    }
  };





  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'facebook': return <FacebookIcon />;
      case 'twitter': return <TwitterIcon />;
      case 'linkedin': return <LinkedInIcon />;
      default: return <HomeIcon />;
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: { xs: 2, sm: 3 },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, mb: { xs: 1, sm: 0 } }}
        >
          {isMobile ? 'Property Listings' : 'Property Advertising & Listings'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setListingDialog(true)}
          fullWidth={isMobile}
          size={isMobile ? 'small' : 'medium'}
        >
          {isMobile ? 'New Listing' : 'Create New Listing'}
        </Button>
      </Box>

      <Tabs 
        value={activeTab} 
        onChange={(e, newValue) => setActiveTab(newValue)} 
        sx={{ mb: { xs: 2, sm: 3 } }}
        variant={isMobile ? 'scrollable' : 'standard'}
        scrollButtons="auto"
      >
        <Tab label="Properties" />
        <Tab label="Active Listings" />
        <Tab label="Syndication" />
        <Tab label="Analytics" />
      </Tabs>

      {/* Properties Tab */}
      {activeTab === 0 && (
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
          {properties.map((property) => (
            <Grid item xs={12} md={6} key={property.id}>
              <Card>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start', 
                    mb: { xs: 1.5, sm: 2 },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 }
                  }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography 
                        variant="h6"
                        sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, mb: { xs: 0.5, sm: 0 } }}
                      >
                        {property.name}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        <LocationIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.5, verticalAlign: 'middle' }} />
                        {property.address}
                      </Typography>
                    </Box>
                    <Chip 
                      label={`${property.units.length} Units`} 
                      size="small"
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    />
                  </Box>
                  
                  <Typography 
                    variant="subtitle2" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: { xs: 1, sm: 1.5 } }}
                  >
                    Available Units:
                  </Typography>
                  <List dense>
                    {property.units.filter(unit => unit.status === 'available').map((unit) => (
                      <ListItem 
                        key={unit.id} 
                        sx={{ 
                          px: 0,
                          py: { xs: 0.5, sm: 1 },
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          gap: { xs: 1, sm: 0 }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: { xs: 36, sm: 40 } }}>
                          <ApartmentIcon fontSize={isMobile ? 'small' : 'medium'} />
                        </ListItemIcon>
                        <ListItemText
                          primary={unit.name}
                          secondary={`$${unit.rent}/month • ${unit.bedrooms}BR • ${unit.bathrooms}BA`}
                          primaryTypographyProps={{ sx: { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
                          secondaryTypographyProps={{ sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}
                          sx={{ flex: 1, minWidth: 0 }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setSelectedProperty(property);
                            setListingData({
                              ...listingData,
                              price: unit.rent,
                              bedrooms: unit.bedrooms,
                              bathrooms: unit.bathrooms
                            });
                            setListingDialog(true);
                          }}
                          fullWidth={isMobile}
                          sx={{ mt: { xs: 1, sm: 0 } }}
                        >
                          {isMobile ? 'Create' : 'Create Listing'}
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Active Listings Tab */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Active Listings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No active listings yet. Create your first listing to get started!
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Syndication Tab */}
      {activeTab === 2 && (
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, mb: { xs: 1.5, sm: 2 } }}
                >
                  Platform Syndication
                </Typography>
                <List>
                  {Object.entries(listingData.platforms).map(([platform, enabled]) => (
                    <ListItem 
                      key={platform}
                      sx={{ px: { xs: 0, sm: 1 }, py: { xs: 0.5, sm: 1 } }}
                    >
                      <ListItemIcon sx={{ minWidth: { xs: 36, sm: 40 } }}>
                        {getPlatformIcon(platform)}
                      </ListItemIcon>
                      <ListItemText
                        primary={platform.charAt(0).toUpperCase() + platform.slice(1)}
                        secondary={enabled ? 'Active' : 'Inactive'}
                        primaryTypographyProps={{ sx: { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
                        secondaryTypographyProps={{ sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}
                      />
                      <Switch
                        checked={enabled}
                        onChange={(e) => setListingData({
                          ...listingData,
                          platforms: {
                            ...listingData.platforms,
                            [platform]: e.target.checked
                          }
                        })}
                        size={isMobile ? 'small' : 'medium'}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, mb: { xs: 1, sm: 1.5 } }}
                >
                  Syndication Status
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: { xs: 1.5, sm: 2 } }}
                >
                  Track the performance of your listings across different platforms.
                </Typography>
                <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
                  <Typography 
                    variant="body2"
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: { xs: 0.5, sm: 1 } }}
                  >
                    <CheckIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: 'success.main', verticalAlign: 'middle' }} />
                    Facebook Marketplace: 15 views
                  </Typography>
                  <Typography 
                    variant="body2"
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    <CheckIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: 'success.main', verticalAlign: 'middle' }} />
                    Craigslist: 8 responses
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Analytics Tab */}
      {activeTab === 3 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Listing Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View performance metrics and insights for your property listings.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Create Listing Dialog */}
      <Dialog 
        open={listingDialog} 
        onClose={() => setListingDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Create New Listing
          {selectedProperty && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ mt: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Property: {selectedProperty.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 1, sm: 1.5 }, pb: { xs: 2, sm: 2 } }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                Property Details
              </Typography>
              
              <TextField
                fullWidth
                label="Listing Title"
                value={listingData.title}
                onChange={(e) => setListingData({ ...listingData, title: e.target.value })}
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Monthly Rent"
                type="number"
                value={listingData.price}
                onChange={(e) => setListingData({ ...listingData, price: e.target.value })}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Bedrooms"
                    type="number"
                    value={listingData.bedrooms}
                    onChange={(e) => setListingData({ ...listingData, bedrooms: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Bathrooms"
                    type="number"
                    value={listingData.bathrooms}
                    onChange={(e) => setListingData({ ...listingData, bathrooms: e.target.value })}
                  />
                </Grid>
              </Grid>
              
              <TextField
                fullWidth
                label="Square Feet"
                type="number"
                value={listingData.squareFeet}
                onChange={(e) => setListingData({ ...listingData, squareFeet: e.target.value })}
                sx={{ mb: 2 }}
              />
              
              <FormControl fullWidth variant="outlined">
                <InputLabel id="listing-property-type-label">Property Type</InputLabel>
                <Select
                  labelId="listing-property-type-label"
                  label="Property Type"
                  value={listingData.propertyType}
                  onChange={(e) => setListingData({ ...listingData, propertyType: e.target.value })}
                >
                  <MenuItem value="apartment">Apartment</MenuItem>
                  <MenuItem value="house">House</MenuItem>
                  <MenuItem value="condo">Condo</MenuItem>
                  <MenuItem value="townhouse">Townhouse</MenuItem>
                  <MenuItem value="studio">Studio</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                AI-Generated Content
              </Typography>
              
              <Button
                variant="outlined"
                startIcon={<AIIcon />}
                onClick={generateAIContent}
                sx={{ mb: 2 }}
              >
                Generate AI Content
              </Button>
              
              {aiContent.title ? (
                <Box>
                  <TextField
                    fullWidth
                    label="AI-Generated Title"
                    value={aiContent.title}
                    sx={{ mb: 2 }}
                    multiline
                    rows={2}
                  />
                  
                  <TextField
                    fullWidth
                    label="AI-Generated Description"
                    value={aiContent.description}
                    sx={{ mb: 2 }}
                    multiline
                    rows={4}
                  />
                  
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Key Features:
                    </Typography>
                    {aiContent.highlights.map((highlight, index) => (
                      <Chip
                        key={index}
                        label={highlight}
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Generate a title, description, and highlights from the property details on the left.
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                Photos
              </Typography>
              <PhotoUpload
                onPhotosChange={(photos) => setListingData({ ...listingData, photos })}
                maxPhotos={10}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                Syndication Platforms
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(listingData.platforms).map(([platform, enabled]) => (
                  <Grid item xs={6} sm={4} key={platform}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={enabled}
                          onChange={(e) => setListingData({
                            ...listingData,
                            platforms: {
                              ...listingData.platforms,
                              [platform]: e.target.checked
                            }
                          })}
                        />
                      }
                      label={platform.charAt(0).toUpperCase() + platform.slice(1)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => {
              setListingDialog(false);
              setListingData({
                title: '',
                description: '',
                price: '',
                bedrooms: '',
                bathrooms: '',
                squareFeet: '',
                propertyType: 'apartment',
                features: [],
                photos: [],
                platforms: {
                  craigslist: true,
                  facebook: true,
                  zillow: false,
                  apartments: false,
                  realtor: false
                }
              });
              setAiContent({ title: '', description: '', highlights: [] });
              setSelectedProperty(null);
            }}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={createListing} 
            variant="contained"
            disabled={!listingData.price || creatingListing}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {creatingListing ? 'Creating...' : 'Create Listing'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PropertyAdvertising; 